import _ from "lodash";
import React from "react";
import i18n from "src/i18n/config";
import type { useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import useDebounce from "src/ui/hooks/useDebounce";
import { showErrorToast } from "src/ui/shared/toasts/Toasts";
import { getErrorMessage } from "src/ui/shared/toasts/useQueryErrorToast";

import { createSource } from "@dashboard/products/components/ProductBuilder/components/Recipes";
import { AndroidModule, Product, Sources } from "@dashboard/products/types";

const partitionOptions = [
    { value: "odm", label: "/odm" },
    { value: "product", label: "/product" },
    { value: "system", label: "/system" },
    { value: "system_ext", label: "/system_ext" },
    { value: "vendor", label: "/vendor" },
];

const addableModuleTemplates: AndroidModule[] = [
    {
        name: "cta_predefined_system_include",
        type: "existing_module",
        tags: ["Build"],
    },
    {
        name: "cta_predefined_system_exclude",
        type: "remove_override",
        tags: ["Build"],
    },
    {
        name: "cta_predefined_own_application",
        type: "prebuilt_app",
        tags: ["Own"],
        sourceId: "",
    },
    {
        name: "cta_predefined_own_executable",
        type: "prebuilt_executable",
        tags: ["Own"],
        sourceId: "",
    },
    {
        name: "com.emteria.update",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.kiosk",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.storage",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.mdm",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "com.emteria.headless",
        type: "existing_module",
        tags: ["System"],
    },
    {
        name: "cta_prebuilt_bootanimation",
        type: "prebuilt_bootanimation",
        tags: ["Own"],
        sourceId: "",
    },
    {
        name: "cta_predefined_downloadable_file",
        type: "downloadable_file",
        tags: ["Build"],
        sourceId: "",
    },
    {
        name: "cta_partition_layout",
        type: "partition_layout",
        tags: ["Build"],
        sourceId: "",
    },
];

// The build accepts at most one of these per product, so their template disappears once one is added.
const singleInstanceModuleTypes: AndroidModule["type"][] = ["prebuilt_bootanimation", "partition_layout"];

// A dialog that saves through this must stay open when it returns false: closing it would throw away what the user
// picked while the toast says nothing was saved.
async function saveProduct(updateProduct: ReturnType<typeof useUpdateProductMutation>[0], product: Product) {
    try {
        await updateProduct(product).unwrap();
        return true;
    } catch (error) {
        showErrorToast(getErrorMessage(error));
        return false;
    }
}

// createSource reuses an existing source id when the picked file already has one, so swapping a file can
// legitimately land on the id we are about to drop. Keep the entry unless nothing references it any more.
function dropUnreferencedSource(
    sources: Sources,
    modules: AndroidModule[],
    previousSourceId: string | undefined,
    sourceId?: string,
) {
    if (!previousSourceId || previousSourceId === sourceId || modules.some((item) => item.sourceId === previousSourceId)) {
        return sources;
    }

    const remainingSources = { ...sources };
    delete remainingSources[previousSourceId];
    return remainingSources;
}

// A dialog holds the module it was opened on, so a product refetch while it is open leaves that reference
// behind. Match on the module's own identity before giving up — a module type can occur many times, so a
// missed match must stop the save rather than append a second copy of what the user meant to change.
function findModuleIndex(modules: AndroidModule[], module: AndroidModule) {
    const index = modules.indexOf(module);
    if (index !== -1) {
        return index;
    }
    return modules.findIndex(
        (item) => item.type === module.type && item.sourceId === module.sourceId && item.name === module.name,
    );
}

function checkFileSourceExists(modules: AndroidModule[], sources: Sources, fileId: string) {
    const sourceId = Object.keys(sources).find((item) => sources[item].fileId === fileId);
    if (!sourceId) {
        return false;
    }

    return modules.some((item) => item.sourceId === sourceId);
}

function filterSelectableFiles(files: API.File[], modules: AndroidModule[], sources: Sources, ownFileId?: string | null) {
    return files.filter((item) => item.id === ownFileId || !checkFileSourceExists(modules, sources, item.id));
}

// Returns null when the edited module is gone from the product, so the caller refuses the save instead of adding a
// second copy of what the user meant to change.
function applyModuleFile<ModuleShape extends AndroidModule>(
    product: Product,
    editedModule: AndroidModule | null,
    file: API.File,
    buildModuleValues: (sourceId: string) => Partial<ModuleShape>,
    newModuleDefaults: Partial<ModuleShape>,
): Product | null {
    const modules: AndroidModule[] = product.customBuildConfig?.modules || [];
    const sources = product.customBuildConfig?.sources || {};
    const previousSourceId = typeof editedModule?.sourceId === "string" ? editedModule.sourceId : undefined;

    const targetIndex = editedModule ? findModuleIndex(modules, editedModule) : -1;
    if (editedModule && targetIndex === -1) {
        return null;
    }

    const [sourceId, updatedSources] = createSource(file, sources);
    const moduleValues = buildModuleValues(sourceId);
    const updatedModules: AndroidModule[] =
        targetIndex === -1
            ? [...modules, { ...newModuleDefaults, ...moduleValues } as AndroidModule]
            : modules.map((item, index) => (index === targetIndex ? ({ ...item, ...moduleValues } as AndroidModule) : item));

    if (!product.customBuildConfig) {
        return { ...product };
    }
    return {
        ...product,
        customBuildConfig: {
            ...product.customBuildConfig,
            modules: updatedModules,
            sources: dropUnreferencedSource(updatedSources, updatedModules, previousSourceId, sourceId),
        },
    };
}

function getTags(modules: AndroidModule[]) {
    const tags = ["Added", "All", "Own", "System"];
    modules.forEach((module) => {
        tags.push(...module.tags);
    });
    const uniqueTags = _.uniq(tags);
    return _.orderBy(uniqueTags, (tag) => tag);
}
function filterModules<T extends AndroidModule>(modules: T[], tag: string): T[] {
    return modules.filter((module) => {
        return tag === "All" || module.tags.includes(tag);
    });
}
function excludeAlreadyAddedModules(templateModules: AndroidModule[], addedModules: AndroidModule[]): AndroidModule[] {
    return templateModules.filter((template) => {
        if (singleInstanceModuleTypes.includes(template.type)) {
            return !addedModules.some((added) => added.type === template.type);
        }
        return !addedModules.some((added) => added.name === template.name);
    });
}
function getProductHeaderTitle(product: Product) {
    const productName = product.customBuildConfig?.productName;
    const version = product.androidVersion;

    if (productName && version) {
        return productName + " - Android " + version;
    }

    if (productName) {
        return productName;
    }

    return i18n.t("sidepanels:modulePanel.unknownProduct");
}

type SearchContextType = {
    search: string;
    onChange: (search: string) => void;
};

const SearchContext = React.createContext<SearchContextType>({
    search: "",
    onChange: () => {},
});

type SearchProviderProps = {
    children: (ctx: SearchContextType) => React.ReactNode;
};
const SearchProvider = (props: SearchProviderProps) => {
    const [search, setSearch] = React.useState("");

    const onChange = useDebounce((value: string) => setSearch(value.toLowerCase()), 200);

    return <SearchContext.Provider value={{ search, onChange }}>{props.children({ search, onChange })}</SearchContext.Provider>;
};

const useSearch = () => {
    const context = React.useContext(SearchContext);
    if (!context) {
        throw new Error("useSearch must be used within a SearchProvider");
    }
    return context;
};

export default {
    addableModuleTemplates,
    applyModuleFile,
    dropUnreferencedSource,
    excludeAlreadyAddedModules,
    filterModules,
    filterSelectableFiles,
    findModuleIndex,
    getProductHeaderTitle,
    getTags,
    partitionOptions,
    saveProduct,
    SearchProvider,
    useSearch,
};
